"use client";

import React from "react";
import Navbar from "@/components/Navbar";
import Footer2 from "@/components/landing-page/Footer2";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white text-black selection:bg-indigo-100 relative flex flex-col">
      <Navbar isLoggedIn={false} />

      <main className="pt-24 pb-16 flex-grow">

        {/* Hero ,  Left-aligned, editorial style */}
        <section className="px-6 max-w-4xl mx-auto mb-24 pt-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
              About Senera Core
            </p>
            <h1 className="text-3xl md:text-[2.75rem] font-semibold tracking-tight text-black leading-[1.15] mb-8">
              AI will have a vast impact on the world. <br className="hidden md:block" />
              We make sure you're on the right side of it.
            </h1>
            <div className="w-16 h-[2px] bg-zinc-950 mb-8"></div>
            <p className="text-base md:text-[17px] text-zinc-700 leading-[1.8] max-w-2xl">
              We are an agile, AI-first product studio. No bloated management layers,
              no endless discovery phases. You work directly with senior engineers who
              figure out exactly how to implement AI into your business, build the
              systems, and run them for you.
            </p>
          </motion.div>
        </section>

        {/* Metrics ,  Horizontal rule style, not cards */}
        <section className="px-6 max-w-4xl mx-auto mb-24">
          <div className="border-t border-zinc-200">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                { value: "40+", label: "Systems Deployed" },
                { value: "3", label: "Countries" },
                { value: "48h", label: "Average Prototype Delivery" },
                { value: "99.9%", label: "System Uptime" },
              ].map((metric, i) => (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  key={i}
                  className={`py-8 pr-6 ${i < 3 ? "border-r border-zinc-200" : ""} ${i < 2 ? "border-b md:border-b-0 border-zinc-200" : i >= 2 ? "border-b-0" : ""}`}
                >
                  <div className="pl-6">
                    <span className="text-3xl md:text-4xl font-bold text-black block mb-1 tracking-tight">{metric.value}</span>
                    <span className="text-[10px] text-zinc-600 font-semibold uppercase tracking-[0.15em]">{metric.label}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How We Operate ,  Alternating left-right blocks, not cards */}
        <section className="px-6 max-w-4xl mx-auto mb-24">
          <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.2em] mb-10">
            How We Operate
          </p>

          <div className="space-y-16">
            {/* Principle 1 */}
            <div className="grid md:grid-cols-[140px_1fr] gap-6 items-start">
              <div>
                <span className="text-5xl font-bold text-zinc-300 leading-none">01</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Direct Engineer Access</h3>
                <p className="text-zinc-700 text-sm leading-[1.8] max-w-lg">
                  We eliminate account managers and middle-men. You communicate directly
                  with the senior architects and engineers building your automation
                  systems ,  ensuring rapid iterations and zero miscommunication.
                </p>
              </div>
            </div>

            <div className="h-px bg-zinc-100 w-full"></div>

            {/* Principle 2 */}
            <div className="grid md:grid-cols-[140px_1fr] gap-6 items-start">
              <div>
                <span className="text-5xl font-bold text-zinc-300 leading-none">02</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Built for Operators, Not Engineers</h3>
                <p className="text-zinc-700 text-sm leading-[1.8] max-w-lg">
                  We don't build software that only developers can use. Every AI system
                  comes with intuitive dashboards and clear workflows so your existing
                  team can manage them without writing a single line of code.
                </p>
              </div>
            </div>

            <div className="h-px bg-zinc-100 w-full"></div>

            {/* Principle 3 */}
            <div className="grid md:grid-cols-[140px_1fr] gap-6 items-start">
              <div>
                <span className="text-5xl font-bold text-zinc-300 leading-none">03</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Measurable ROI Focus</h3>
                <p className="text-zinc-700 text-sm leading-[1.8] max-w-lg">
                  We only take on projects where we can move the needle. Whether it's
                  cutting overhead by 40% or increasing sales conversions by 2x, every
                  system is built with a strict focus on immediate financial return.
                </p>
              </div>
            </div>

            <div className="h-px bg-zinc-100 w-full"></div>

            {/* Principle 4 */}
            <div className="grid md:grid-cols-[140px_1fr] gap-6 items-start">
              <div>
                <span className="text-5xl font-bold text-zinc-300 leading-none">04</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Enterprise-Grade Security</h3>
                <p className="text-zinc-700 text-sm leading-[1.8] max-w-lg">
                  Security isn't an afterthought. We build using private, siloed
                  environments, ensuring your proprietary data is never used to train
                  public AI models and always remains fully compliant.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA ,  Clean, no card wrapper */}
        <section className="px-6 max-w-4xl mx-auto mb-16">
          <div className="border-t border-zinc-200 pt-16 text-center">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-3">
              Ready to make your business AI-native?
            </h2>
            <p className="text-zinc-600 text-sm max-w-md mx-auto mb-8">
              Start with a 30-minute strategy call. No pitch, no pressure.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              Book Your Free Strategy Call
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

      </main>

      <Footer2 />
    </div>
  );
}
